import { useRevisionGetUsersQuery } from '@app/services/auto-generated/revision'
import {
  Autocomplete,
  Button,
  InputAdornment,
  inputBaseClasses,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Box,
  Chip,
} from '@common/components'
import { SearchIcon } from '@common/icons'
import { useAppSelector } from '@common/hooks'
import { selectActiveRole } from '@features/auth/auth-slice'
import React from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

const DashboardFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const activeRole = useAppSelector(selectActiveRole)
  const [filters, setFilters] = React.useState({
    years: searchParams.get('years')?.split(',') || ['all'],
    cohorts: searchParams.get('cohorts')?.split(',') || ['all'],
    teacherIds: searchParams.get('teacherIds')?.split(',') || ['all'],
    studentIds: searchParams.get('studentIds')?.split(',') || ['all'],
  })
  const [searchTerm, setSearchTerm] = React.useState('')

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value)
  }

  const handleFilterChange = (filterType: keyof typeof filters) => (event) => {
    const selectedValues: string[] = event.target.value
    let newValues: string[]

    // If 'all' is being selected
    if (selectedValues.includes('all')) {
      // If the previous state didn't include 'all', set to ['all']
      if (!filters[filterType].includes('all')) {
        newValues = ['all']
      } else {
        // If 'all' was already selected, remove it
        newValues = selectedValues.filter((value) => value !== 'all')
      }
    } else {
      // For non-'all' selections
      newValues = selectedValues.filter((value) => value !== 'all')
      // If nothing is selected, default to ['all']
      if (newValues.length === 0) {
        newValues = ['all']
      }
    }

    setFilters((prev) => ({
      ...prev,
      [filterType]: newValues,
    }))
  }

  const handleApplyFilters = () => {
    // Create new URLSearchParams object
    const newSearchParams = new URLSearchParams()

    // Only add parameters that aren't 'all'
    Object.entries(filters).forEach(([key, values]) => {
      if (!values.includes('all') && values.length > 0) {
        newSearchParams.set(key, values.join(','))
      }
    })

    // Update URL with new search params
    setSearchParams(newSearchParams)
  }

  const { data: studentFilterData } = useRevisionGetUsersQuery({ roleFilter: 'student' })
  const { data: teacherFilterData } = useRevisionGetUsersQuery({ roleFilter: 'teacher' })

  const handleResetFilters = () => {
    const defaultFilters = {
      years: ['all'],
      cohorts: ['all'],
      teacherIds: ['all'],
      studentIds: ['all'],
    }
    setFilters(defaultFilters)
    setSearchParams(new URLSearchParams()) // Clear URL params
  }

  // Handle student selection with Autocomplete
  const handleStudentChange = (event, newValue) => {
    // If nothing is selected, default to 'all'
    if (!newValue || newValue.length === 0) {
      setFilters((prev) => ({ ...prev, studentIds: ['all'] }))
      return
    }

    // Check if "All" is being selected
    const allOptionSelected = newValue.some((option) => option.value === 'all')
    const previouslyHadAll = filters.studentIds.includes('all')

    if (allOptionSelected && !previouslyHadAll) {
      // User just selected "All", so only keep "All"
      setFilters((prev) => ({ ...prev, studentIds: ['all'] }))
      return
    } else if (allOptionSelected && previouslyHadAll && newValue.length > 1) {
      // User had "All" selected and selected something else, remove "All"
      const filteredValues = newValue
        .filter((option) => option.value !== 'all')
        .map((option) => option.value)
      setFilters((prev) => ({ ...prev, studentIds: filteredValues }))
      return
    }

    // Normal case: just use the selected values
    const selectedIds = newValue.map((option) => option.value)
    setFilters((prev) => ({ ...prev, studentIds: selectedIds }))
  }

  // Format student data for Autocomplete
  const studentOptions = React.useMemo(() => {
    const options = [{ label: 'All', value: 'all' }]
    if (studentFilterData?.length) {
      studentFilterData.forEach((student) => {
        options.push({
          label: student.username,
          value: student.user_id.toString(),
        })
      })
    }
    return options
  }, [studentFilterData])

  // Get current selected student options
  const selectedStudentOptions = React.useMemo(() => {
    if (filters.studentIds.includes('all')) {
      return [studentOptions.find((opt) => opt.value === 'all')].filter(Boolean)
    }

    return filters.studentIds
      .map((id) => studentOptions.find((opt) => opt.value === id))
      .filter(Boolean) // Remove any undefined values
  }, [filters.studentIds, studentOptions])

  // Handle teacher selection with Autocomplete
  const handleTeacherChange = (event, newValue) => {
    // If nothing is selected, default to 'all'
    if (!newValue || newValue.length === 0) {
      setFilters((prev) => ({ ...prev, teacherIds: ['all'] }))
      return
    }

    // Check if "All" is being selected
    const allOptionSelected = newValue.some((option) => option.value === 'all')
    const previouslyHadAll = filters.teacherIds.includes('all')

    if (allOptionSelected && !previouslyHadAll) {
      // User just selected "All", so only keep "All"
      setFilters((prev) => ({ ...prev, teacherIds: ['all'] }))
      return
    } else if (allOptionSelected && previouslyHadAll && newValue.length > 1) {
      // User had "All" selected and selected something else, remove "All"
      const filteredValues = newValue
        .filter((option) => option.value !== 'all')
        .map((option) => option.value)
      setFilters((prev) => ({ ...prev, teacherIds: filteredValues }))
      return
    }

    // Normal case: just use the selected values
    const selectedIds = newValue.map((option) => option.value)
    setFilters((prev) => ({ ...prev, teacherIds: selectedIds }))
  }

  // Format teacher data for Autocomplete
  const teacherOptions = React.useMemo(() => {
    const options = [{ label: 'All', value: 'all' }]
    if (teacherFilterData?.length) {
      teacherFilterData.forEach((teacher) => {
        options.push({
          label: teacher.username,
          value: teacher.user_id.toString(),
        })
      })
    }
    return options
  }, [teacherFilterData])

  // Get current selected teacher options
  const selectedTeacherOptions = React.useMemo(() => {
    if (filters.teacherIds.includes('all')) {
      return [teacherOptions.find((opt) => opt.value === 'all')].filter(Boolean)
    }

    return filters.teacherIds
      .map((id) => teacherOptions.find((opt) => opt.value === id))
      .filter(Boolean) // Remove any undefined values
  }, [filters.teacherIds, teacherOptions])

  const yearOptions = ['all', '2024', '2025']
  const cohortOptions = [
    'all',
    '2024 B Standard',
    '2024 TPC',
    '2024 UEEC 10 T9',
    'UEEC 10 T10',
    'Standard C',
    '2025 UEEC 10 T2',
  ]

  return (
    <>
      <Typography variant='h6' sx={{ alignSelf: 'flex-start' }}>
        Filter:
      </Typography>
      <Stack direction={'row'} alignItems={'center'} sx={{ width: '100%', mt: 3 }} spacing={3}>
        <Typography sx={{ width: '35%', pl: 0.5 }}>Year</Typography>
        <Select
          size='small'
          value={filters.years}
          onChange={handleFilterChange('years')}
          fullWidth
          multiple
        >
          {yearOptions.map((year) => (
            <MenuItem key={year} value={year}>
              {year === 'all' ? 'All' : year}
            </MenuItem>
          ))}
        </Select>
      </Stack>
      <Stack direction={'row'} alignItems={'center'} sx={{ width: '100%', mt: 3 }} spacing={3}>
        <Typography sx={{ width: '35%', pl: 0.5 }}>Cohort</Typography>
        <Select
          size='small'
          value={filters.cohorts}
          onChange={handleFilterChange('cohorts')}
          fullWidth
          multiple
          sx={{
            [`& .${inputBaseClasses.root}`]: {
              flexWrap: 'wrap',
            },
            '& .MuiSelect-select': {
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px',
            },
          }}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip
                  key={value}
                  label={value === 'all' ? 'All' : value}
                  size='small'
                  onDelete={() => {
                    const newValues = filters.cohorts.filter((item) => item !== value)
                    // If removing the last item, set to 'all'
                    if (newValues.length === 0) {
                      setFilters((prev) => ({ ...prev, cohorts: ['all'] }))
                    } else {
                      setFilters((prev) => ({ ...prev, cohorts: newValues }))
                    }
                  }}
                  onMouseDown={(event) => {
                    event.stopPropagation() // Prevent the select from opening when clicking the chip
                  }}
                />
              ))}
            </Box>
          )}
        >
          {cohortOptions.map((cohort) => (
            <MenuItem key={cohort} value={cohort}>
              {cohort === 'all' ? 'All' : cohort}
            </MenuItem>
          ))}
        </Select>
      </Stack>
      {activeRole !== 'admin' && (
        <Stack direction={'row'} alignItems={'center'} sx={{ width: '100%', mt: 3 }} spacing={3}>
          <Typography sx={{ width: '35%', pl: 0.5 }}>Teacher</Typography>
          <Autocomplete
            multiple
            size='small'
            options={teacherOptions}
            value={selectedTeacherOptions}
            onChange={handleTeacherChange}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedTeacherOptions.length === 0 ? 'Select teachers' : ''}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position='start'>
                        <SearchIcon fontSize='small' />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            fullWidth
          />
        </Stack>
      )}
      <Stack direction={'row'} alignItems={'center'} sx={{ width: '100%', mt: 3 }} spacing={3}>
        <Typography sx={{ width: '35%', pl: 0.5 }}>Student</Typography>
        <Autocomplete
          multiple
          size='small'
          options={studentOptions}
          value={selectedStudentOptions}
          onChange={handleStudentChange}
          getOptionLabel={(option) => option.label}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={selectedStudentOptions.length === 0 ? 'Select students' : ''}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position='start'>
                      <SearchIcon fontSize='small' />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          fullWidth
        />
      </Stack>
      <Stack direction='row' spacing={2} sx={{ ml: 'auto', mt: 3 }}>
        <Button variant='outlined' color='neutral' onClick={handleResetFilters}>
          Reset
        </Button>
        <Button variant='contained' color='neutral' onClick={handleApplyFilters}>
          Filter
        </Button>
      </Stack>
    </>
  )
}

export default DashboardFilter
